import { Loader, Switch, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
    clearAttributeValue,
    getEntityMasterFilterOptions,
    patchCoreField,
    upsertAttributeValue,
    type MasterTableColumnMeta,
} from '@api/entity-attributes';
import { getMaterialStockSnapshot, type MaterialStockSnapshotRow } from '@api/material-stock-snapshot';
import { buildUnitOptions } from '@api/materials';
import { runMrpPlan, type MrpContributingProduct, type MrpRateBasis } from '@api/mrp';
import { createVendorMaterialMapping, deleteVendorMaterialMapping, listVendors, vendorInfoOf, type Vendor, type VendorInfo } from '@api/vendors';
import PageHeader from '@components/common/PageHeader';
import AddVendorModal from '@components/containers/EntityMaster/AddVendorModal';
import AddVendorPriceModal, { type VendorPricePreset } from '@components/containers/EntityMaster/AddVendorPriceModal';
import InlineField from '@components/containers/EntityMaster/InlineField';
import { loadCurrencyOptions, type MakerDraft, type SelectOption } from '@components/containers/EntityMaster/makerOptions';
import MakerRows, { SUPPLIER_LABELS, type MakerRowMeta, type SavedMaker } from '@components/containers/EntityMaster/MakerRows';
import { PriceHistoryModal } from '@components/containers/EntityMaster/PriceHistoryModal';
import { contributingProductColumns } from '@components/containers/productionPlanning/contributingProductColumns';
import DataSheet from '@components/ui/DataSheet';
import SaveState, { type SaveStatus } from '@components/ui/SaveState';
import StatusPill from '@components/ui/StatusPill';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { semanticColors } from '@styles/design-system';
import { readApiError } from '@utils/apiError';
import { formatMaterialQuantity } from '@utils/materialQuantity';
import { COMMON_GST_RATES } from '@utils/tax';

import { cell, loadMappingRows, loadMasterRow } from './masterRow';
import { BackToCatalog } from './ProductRecord';
import styles from './record.module.css';

const NUMERIC_KEYS = new Set(['cost', 'gstRate']);
const DECIMALS: Record<string, number> = { cost: 4, gstRate: 2 };
const HANDLED_KEYS = new Set(['sku', 'isActive', 'materialType', 'vendors', 'usedIn', 'name', 'unit']);
const MATERIAL_TYPES = ['RM', 'PM', 'STAGE'];
/** Columns offered as a pick list: the brand's own values, or a fixed set, free typing kept. */
type ListedKey = 'category' | 'gstRate';
const MATERIAL_TYPE_TONE: Record<string, 'info' | 'warning' | 'neutral'> = { RM: 'info', PM: 'warning', STAGE: 'neutral' };

/**
 * One material, on a page of its own: details, who supplies it, which products use it, and
 * its stock. Mirrors the product page; the supplier table is the maker table with the
 * material's words on it, and "Used in" is the MRP plan's own rows for this one material.
 */
const MaterialRecord: React.FC = () => {
    const { materialId = '' } = useParams<{ materialId: string }>();
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const isAdmin = currentUser?.role === 'ADMIN';

    const [row, setRow] = useState<Record<string, unknown> | null>(null);
    const [columns, setColumns] = useState<MasterTableColumnMeta[]>([]);
    const [missing, setMissing] = useState(false);
    const [overrides, setOverrides] = useState<Record<string, string>>({});
    const [activeStatus, setActiveStatus] = useState<SaveStatus>('idle');
    const [activeError, setActiveError] = useState<string | null>(null);
    // The grid's own filter options double as the pick lists: category and unit.
    const [lists, setLists] = useState<Record<string, string[]>>({});

    const [vendorNames, setVendorNames] = useState<Map<string, VendorInfo>>(new Map());
    const [vendorOptions, setVendorOptions] = useState<SelectOption[]>([]);
    const [currencyOptions, setCurrencyOptions] = useState<string[]>([]);
    const [suppliers, setSuppliers] = useState<SavedMaker[]>([]);
    const [drafts, setDrafts] = useState<MakerDraft[]>([]);
    const [savedMeta, setSavedMeta] = useState<Record<string, MakerRowMeta>>({});
    const [draftMeta, setDraftMeta] = useState<Record<number, MakerRowMeta>>({});
    const [historyRow, setHistoryRow] = useState<SavedMaker | null>(null);
    const [pricePreset, setPricePreset] = useState<VendorPricePreset | null>(null);
    const [newVendorOpen, setNewVendorOpen] = useState(false);

    const [usedIn, setUsedIn] = useState<MrpContributingProduct[] | null>(null);
    const [usedInBasis, setUsedInBasis] = useState<MrpRateBasis>('BACKWARD');
    const [stockRows, setStockRows] = useState<MaterialStockSnapshotRow[]>([]);

    const valueOf = useCallback(
        (key: string): string | null => (key in overrides ? overrides[key] : cell(row, key)),
        [overrides, row],
    );
    const materialName = valueOf('name') || materialId;
    const unit = valueOf('unit') || 'pcs';

    // ---- reads ---------------------------------------------------------------------------

    const loadRow = useCallback(async () => {
        const result = await loadMasterRow('MATERIAL', materialId).catch(() => null);
        if (!result) {
            setMissing(true);
            return;
        }
        setRow(result.row);
        setColumns(result.columns);
        setOverrides({});
    }, [materialId]);

    const loadSuppliers = useCallback(async () => {
        try {
            const rows = await loadMappingRows('VENDOR_MATERIAL_MAPPING', materialId);
            setSuppliers(
                rows.map((r) => ({
                    rowId: String(r._rowId ?? r._id ?? ''),
                    vendorId: String(r.vendorId ?? ''),
                    vendorName: '',
                    leadTime: null,
                    price: cell(r, 'cost'),
                    currency: cell(r, 'currency'),
                    term: cell(r, 'priority'),
                    priceSince: cell(r, 'priceSince'),
                    priceHistory: cell(r, 'priceHistory'),
                })),
            );
        } catch {
            setSuppliers([]);
        }
    }, [materialId]);

    const loadContext = useCallback(async () => {
        void getMaterialStockSnapshot({ materialIds: [materialId] })
            .then((r) => setStockRows(r.rows.filter((s) => s.materialId === materialId)))
            .catch(() => setStockRows([]));
        void runMrpPlan({ materialIds: [materialId] })
            .then((plan) => {
                setUsedInBasis(plan.config.basis);
                setUsedIn(plan.rows.find((r) => r.materialId === materialId)?.contributingProducts ?? []);
            })
            .catch(() => setUsedIn([]));
    }, [materialId]);

    useEffect(() => {
        setMissing(false);
        setRow(null);
        setDrafts([]);
        setDraftMeta({});
        setSavedMeta({});
        setUsedIn(null);
        setActiveStatus('idle');
        void loadRow();
        void loadSuppliers();
        void loadContext();
    }, [loadRow, loadSuppliers, loadContext]);

    useEffect(() => {
        void loadCurrencyOptions().then(setCurrencyOptions);
        void getEntityMasterFilterOptions('MATERIAL').then(setLists).catch(() => undefined);
        void listVendors()
            .then((vendors) => {
                setVendorNames(new Map(vendors.map((v) => [v.vendorId, vendorInfoOf(v)])));
                setVendorOptions(
                    vendors.map((v) => ({ value: v.vendorId, label: v.name && v.name !== v.vendorId ? `${v.name} — ${v.vendorId}` : v.vendorId })),
                );
            })
            .catch(() => undefined);
    }, []);

    const namedSuppliers = useMemo(
        () => suppliers.map((s) => ({ ...s, vendorName: vendorNames.get(s.vendorId)?.name ?? s.vendorName, leadTime: vendorNames.get(s.vendorId)?.leadTime ?? null })),
        [suppliers, vendorNames],
    );

    // ---- details -------------------------------------------------------------------------

    const saveCore = useCallback(
        async (key: string, value: string) => {
            await patchCoreField('MATERIAL', materialId, key, value);
            setOverrides((prev) => ({ ...prev, [key]: value }));
        },
        [materialId],
    );

    const isActive = (valueOf('isActive') ?? 'true') !== 'false';

    const saveActive = async (checked: boolean) => {
        setActiveStatus('saving');
        setActiveError(null);
        try {
            await saveCore('isActive', checked ? 'true' : 'false');
            setActiveStatus('saved');
        } catch (err) {
            setActiveError(readApiError(err, 'Could not save'));
            setActiveStatus('failed');
        }
    };

    const confirmActive = (checked: boolean) => {
        if (checked) {
            void saveActive(true);
            return;
        }
        modals.openConfirmModal({
            title: `Deactivate ${materialName}?`,
            children: (
                <Text size="sm">
                    An inactive material drops out of the pickers for recipes and purchase orders. Recipes that
                    already use it, its stock and its suppliers stay as they are.
                </Text>
            ),
            labels: { confirm: 'Deactivate', cancel: 'Keep active' },
            confirmProps: { color: 'red' },
            onConfirm: () => void saveActive(false),
        });
    };

    // ---- suppliers -----------------------------------------------------------------------

    const setSaved = (rowId: string, meta: MakerRowMeta) =>
        setSavedMeta((prev) => ({ ...prev, [rowId]: { ...prev[rowId], ...meta } }));
    const setDraft = (index: number, meta: MakerRowMeta) =>
        setDraftMeta((prev) => ({ ...prev, [index]: { ...prev[index], ...meta } }));

    const onSavedChange = async (supplier: SavedMaker, patch: { currency?: string | null; term?: string }) => {
        setSaved(supplier.rowId, { status: 'saving', error: null });
        try {
            // The mapping grid addresses a material mapping by its pair key, as the makers are.
            const pairKey = `${materialId}::${supplier.vendorId}`;
            if (patch.currency !== undefined) await patchCoreField('VENDOR_MATERIAL_MAPPING', pairKey, 'currency', patch.currency ?? '');
            if (patch.term !== undefined) await patchCoreField('VENDOR_MATERIAL_MAPPING', pairKey, 'priority', patch.term);
            setSaved(supplier.rowId, { status: 'saved' });
            await loadSuppliers();
        } catch (err) {
            setSaved(supplier.rowId, { status: 'failed', error: readApiError(err, 'Could not save') });
        }
    };

    const commitDraft = async (index: number) => {
        const draft = drafts[index];
        if (!draft?.vendorId) return;
        if (suppliers.some((s) => s.vendorId === draft.vendorId)) {
            setDraft(index, { status: 'failed', error: 'Already a supplier of this material' });
            return;
        }
        setDraft(index, { status: 'saving', error: null });
        try {
            await createVendorMaterialMapping({
                materialId,
                vendorId: draft.vendorId,
                cost: draft.price === '' || !isAdmin ? undefined : Number(draft.price),
                currency: draft.currency ?? undefined,
            });
            setDrafts((prev) => prev.filter((_, i) => i !== index));
            setDraftMeta({});
            await loadSuppliers();
            void loadRow();
        } catch (err) {
            setDraft(index, { status: 'failed', error: readApiError(err, 'Could not add the supplier') });
        }
    };

    const removeSupplier = (supplier: SavedMaker) => {
        modals.openConfirmModal({
            title: `Remove ${supplier.vendorName || supplier.vendorId} as a supplier?`,
            children: (
                <Text size="sm">
                    {materialName} will no longer be bought from this supplier. Its current cost line ends today and
                    stays in the history; receipts and orders already recorded are not touched.
                </Text>
            ),
            labels: { confirm: 'Remove', cancel: 'Keep' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setSaved(supplier.rowId, { status: 'saving', error: null });
                try {
                    await deleteVendorMaterialMapping(supplier.rowId);
                    await loadSuppliers();
                    void loadRow();
                } catch (err) {
                    setSaved(supplier.rowId, { status: 'failed', error: readApiError(err, 'Could not remove the supplier') });
                }
            },
        });
    };

    const onVendorCreated = (vendor: Vendor) => {
        setVendorOptions((prev) => [...prev, { value: vendor.vendorId, label: `${vendor.name} — ${vendor.vendorId}` }]);
        setVendorNames((prev) => new Map(prev).set(vendor.vendorId, vendorInfoOf(vendor)));
        setDrafts((prev) => [...prev, { vendorId: vendor.vendorId, price: '', currency: null }]);
        setNewVendorOpen(false);
    };

    // ---- render --------------------------------------------------------------------------

    if (missing) {
        return (
            <div className="flex h-full w-full flex-col gap-4 page-shell">
                <PageHeader title="Material not found" subtitle={materialId} actions={<BackToCatalog view="materials" />} />
            </div>
        );
    }
    if (!row) {
        return (
            <div className="flex h-full w-full items-center justify-center page-shell">
                <Loader size="sm" />
            </div>
        );
    }

    const detailColumns = columns.filter((c) => c.editable && !HANDLED_KEYS.has(c.key));
    const listed: Record<ListedKey, string[]> = { category: lists.category ?? [], gstRate: COMMON_GST_RATES };
    const materialType = (valueOf('materialType') ?? '').toUpperCase();
    const onHand = stockRows.reduce((sum, s) => sum + s.quantity, 0);
    const dailyUse = (usedIn ?? []).reduce((sum, p) => sum + p.contributionToDailyDemand, 0);

    return (
        <div className="flex h-full w-full flex-col gap-4 page-shell">
            <PageHeader
                title={materialName}
                subtitle={[valueOf('sku'), valueOf('category')].filter(Boolean).join(' · ')}
                actions={<BackToCatalog view="materials" />}
            />
            <div className={styles.layout}>
                <div className={styles.main}>
                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <span className={styles.sectionNumber}>1</span>
                            <span className={styles.sectionTitle}>Details</span>
                            <span className={styles.sectionSub}>fields save as you leave them</span>
                        </div>
                        <div className={styles.grid}>
                            <div className={styles.span2}>
                                <InlineField label="Name" value={valueOf('name')} onSave={(v) => saveCore('name', v)} />
                            </div>
                            <InlineField
                                label="Code"
                                value={valueOf('sku')}
                                onSave={async () => undefined}
                                readOnlyReason="The code is the key across recipes, supplier mappings and stock; it is changed by a catalogue cleanup, not here"
                                mono
                            />
                            <InlineField label="Type" value={valueOf('materialType')} kind="autocomplete" options={MATERIAL_TYPES} onSave={(v) => saveCore('materialType', v.toUpperCase())} />
                            <InlineField label="Unit" value={valueOf('unit')} kind="autocomplete" options={buildUnitOptions(lists.unit ?? [])} onSave={(v) => saveCore('unit', v)} hint="Recipe quantities and stock are in this unit" />
                            {detailColumns.map((col) => (
                                <InlineField
                                    key={col.key}
                                    label={col.header}
                                    value={valueOf(col.key)}
                                    kind={listed[col.key as ListedKey] ? 'autocomplete' : NUMERIC_KEYS.has(col.key) ? 'number' : 'text'}
                                    options={listed[col.key as ListedKey]}
                                    decimalScale={DECIMALS[col.key]}
                                    onSave={async (v) => {
                                        if (col.type === 'ATTRIBUTE' && col.attributeId) {
                                            if (v === '') await clearAttributeValue(col.attributeId, materialId);
                                            else await upsertAttributeValue(col.attributeId, materialId, v);
                                            setOverrides((prev) => ({ ...prev, [col.key]: v }));
                                            return;
                                        }
                                        await saveCore(col.key, v);
                                    }}
                                />
                            ))}
                        </div>
                    </section>

                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <span className={styles.sectionNumber}>2</span>
                            <span className={styles.sectionTitle}>Suppliers and costs</span>
                            <span className={styles.sectionCount}>
                                <StatusPill label={`${suppliers.length} ${suppliers.length === 1 ? 'supplier' : 'suppliers'}`} tone={suppliers.length ? 'positive' : 'warning'} />
                            </span>
                        </div>
                        <MakerRows
                            labels={SUPPLIER_LABELS}
                            makers={namedSuppliers}
                            drafts={drafts}
                            vendorOptions={vendorOptions}
                            currencyOptions={currencyOptions}
                            isAdmin={isAdmin}
                            onSavedChange={(s, patch) => void onSavedChange(s, patch)}
                            onSetPrice={(s) => setPricePreset({ vendorId: s.vendorId, itemType: 'material', itemId: materialId, itemLabel: materialName })}
                            onShowHistory={setHistoryRow}
                            onRemove={removeSupplier}
                            savedMeta={(rowId) => savedMeta[rowId]}
                            onDraftChange={(i, patch) => setDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)))}
                            onDraftCommit={(i) => void commitDraft(i)}
                            onDraftRemove={(i) => {
                                setDrafts((prev) => prev.filter((_, j) => j !== i));
                                setDraftMeta({});
                            }}
                            draftMeta={(i) => draftMeta[i]}
                            onAdd={() => setDrafts((prev) => [...prev, { vendorId: null, price: '', currency: null }])}
                            onNewVendor={isAdmin ? () => setNewVendorOpen(true) : undefined}
                        />
                        <span className={styles.footer}>Priority 1 is bought from first. A cost change starts a new dated cost; the old one stays in the history.</span>
                    </section>

                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <span className={styles.sectionNumber}>3</span>
                            <span className={styles.sectionTitle}>Used in</span>
                            <span className={styles.sectionSub}>products whose recipe consumes it, read-only</span>
                            <span className={styles.sectionCount}>
                                {usedIn && <StatusPill label={`${usedIn.length} ${usedIn.length === 1 ? 'product' : 'products'}`} tone={usedIn.length ? 'positive' : 'neutral'} />}
                            </span>
                        </div>
                        {usedIn === null ? (
                            <Loader size="xs" />
                        ) : usedIn.length === 0 ? (
                            <span className={styles.muted}>No recipe uses this material yet.</span>
                        ) : (
                            <DataSheet<MrpContributingProduct>
                                data={[...usedIn].sort((a, b) => b.contributionToDailyDemand - a.contributionToDailyDemand)}
                                columns={contributingProductColumns(unit, usedInBasis)}
                                getRowKey={(p) => p.productId}
                                onRowClick={(p) => void navigate(`/catalog/products/${encodeURIComponent(p.productId)}`)}
                                bare
                                hideHeaderBar
                                maxHeight="auto"
                            />
                        )}
                    </section>
                </div>

                <aside className={styles.rail}>
                    <div className={styles.card}>
                        <span className={styles.cardTitle}>Status</span>
                        <div className={styles.factRow}>
                            <Switch size="sm" color={semanticColors.info.primary} label="Active" checked={isActive} onChange={(e) => confirmActive(e.currentTarget.checked)} disabled={activeStatus === 'saving'} />
                            <SaveState status={activeStatus} message={activeError} />
                        </div>
                        <div className={styles.factRow}>
                            <span>Type</span>
                            {materialType ? <StatusPill label={materialType} tone={MATERIAL_TYPE_TONE[materialType] ?? 'neutral'} /> : <span className={styles.muted}>not set</span>}
                        </div>
                        <div className={styles.factRow}>
                            <span>Suppliers</span>
                            <span>{suppliers.length}</span>
                        </div>
                        <div className={styles.factRow}>
                            <span>Used by</span>
                            <span>{usedIn ? `${usedIn.length} products` : '…'}</span>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <span className={styles.cardTitle}>Stock</span>
                        {stockRows.length === 0 ? (
                            <span className={styles.muted}>No stock on file.</span>
                        ) : (
                            <>
                                <div className={styles.factRow}>
                                    <span>On hand</span>
                                    <span>{formatMaterialQuantity(onHand, unit)}</span>
                                </div>
                                {dailyUse > 0 && (
                                    <div className={styles.factRow}>
                                        <span>Use per day</span>
                                        <span>{formatMaterialQuantity(dailyUse, unit)}</span>
                                    </div>
                                )}
                                {dailyUse > 0 && (
                                    <div className={styles.factRow}>
                                        <span>Days on hand</span>
                                        <span>{(onHand / dailyUse).toFixed(1)}</span>
                                    </div>
                                )}
                                {stockRows.map((s) => (
                                    <div key={`${s.facilityId}`} className={styles.factRow}>
                                        <span className={styles.muted}>{s.facilityName ?? s.facilityId ?? 'Unplaced'}</span>
                                        <span>{formatMaterialQuantity(s.quantity, unit)}</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </aside>
            </div>

            <AddVendorModal opened={newVendorOpen} onClose={() => setNewVendorOpen(false)} onCreated={onVendorCreated} />
            {pricePreset && (
                <AddVendorPriceModal opened onClose={() => setPricePreset(null)} onCreated={() => void loadSuppliers()} preset={pricePreset} />
            )}
            <PriceHistoryModal
                opened={!!historyRow}
                onClose={() => setHistoryRow(null)}
                title={historyRow ? `${materialName} · ${historyRow.vendorName || historyRow.vendorId}` : ''}
                currentPrice={historyRow?.price ?? null}
                currency={historyRow?.currency ?? null}
                priceSince={historyRow?.priceSince ?? null}
                historyText={historyRow?.priceHistory ?? null}
            />
        </div>
    );
};

export default MaterialRecord;
